import { FC, useContext, useEffect, useState } from 'react';
import { Box, Grid, List, Typography } from '@mui/material';
import { FsRawNode } from '~/api/types';
import { StorageContext } from '~/hooks';
import useDragAndDropFilesUpload from '~/hooks/useDragAndDropFilesUpload';
import { getInnerPath } from '~/utils';
import { FileListItem, SelectedFilesToDelete } from '..';
import EmptyFolder from './EmptyFolder';
import { filesListBlockStyles, filesListContainerStyles, filesListHeaderStyles } from './styles';

interface FilesListBlockProps {
  files?: FsRawNode[] | null;
}

const FilesListBlock: FC<FilesListBlockProps> = ({ files }) => {
  const [checked, setChecked] = useState<string[]>([]);
  const storageContext = useContext(StorageContext);

  const handleUploadedFiles = async (files: File[]) => {
    const innerFilePath = getInnerPath(storageContext?.lastClickedItem || '');

    await storageContext?.handleUploadedFiles?.(files, innerFilePath);
  };

  const { listeners, dragActive } = useDragAndDropFilesUpload({
    onChange: handleUploadedFiles,
    clearFilesAfterUpload: true,
  });

  const handleToggle = (value: string) => () => {
    setChecked((prev) => {
      const clone = [...prev];
      const currentIndex = clone.indexOf(value);

      if (currentIndex === -1) {
        clone.push(value);
      } else {
        clone.splice(currentIndex, 1);
      }

      return clone;
    });
  };

  useEffect(() => {
    setChecked([]);
  }, [files]);

  return (
    <Box sx={filesListContainerStyles(dragActive)} {...listeners}>
      {files?.length ? (
        <>
          {!!checked.length && (
            <SelectedFilesToDelete
              selectedFiles={files.filter((file) => checked.includes(file.path))}
              removeSelectedIds={() => setChecked([])}
            />
          )}
          <Grid container sx={filesListHeaderStyles}>
            <Grid size={{ xs: 7 }}>
              <Typography variant="bodySStrong">Name</Typography>
            </Grid>
            <Grid size={{ xs: 1.5 }}>
              <Typography variant="bodySStrong">Size</Typography>
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Typography variant="bodySStrong">Last Modified</Typography>
            </Grid>
            <Grid size={{ xs: 1.5 }} sx={{ display: 'flex', justifyContent: 'flex-end', pr: '8px' }}>
              <Typography variant="bodySStrong">Actions</Typography>
            </Grid>
          </Grid>
          <Box sx={filesListBlockStyles}>
            <List sx={{ width: '100%' }}>
              {files?.map((el) => (
                <FileListItem
                  key={el.name}
                  file={el}
                  isChecked={checked.includes(el.path)}
                  handleToggle={handleToggle(el.path)}
                />
              ))}
            </List>
          </Box>
        </>
      ) : (
        <EmptyFolder onUploadedFiles={handleUploadedFiles} />
      )}
    </Box>
  );
};

export default FilesListBlock;
